import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isDraft, original } from 'immer';
import type {
  BorderToken,
  BrandableTokens,
  CanvasSpec,
  ContrastRule,
  DesignTokens,
  FlowRules,
  MenuFormat,
  Node,
  NodeFrame,
  NodeId,
  PageBlueprint,
  ShadowToken,
  TypographyToken,
} from '@menuvera/engine';

import type { FrozenPage } from '@menuvera/engine';
import {
  acceptsChildren,
  cloneNodeWithNewIds,
  duplicateBlueprint as makeBlueprintCopy,
  findNodeIn,
  flattenTree,
  frozenPagesAsBlueprints,
  insertNodeInto,
  isDescendant,
  isFrozenDocument,
  newNodeId,
  removeNodeFrom,
  scrubAssetFromDocument,
  type TemplateDocument,
} from './nodeTree';
import { collectFramedNodeRects } from './canvas/frameUtils';
import { fillEmptyBookPages, missingBookGroups } from './bookKit';
import { findOverlayPlacement } from './canvas/overlayUtils';
import { migrateToFrames } from './migrate/migrateToFrames';
import { detectConstructorLocale, LOCALE_STORAGE_KEY, type ConstructorLocale } from './i18n';
import {
  APPEARANCE_STORAGE_KEY,
  detectConstructorAppearance,
  type ConstructorAppearance,
} from './theme/constructorTheme';
import {
  buildAddedFormatVariant,
  ensureExplicitVariants,
  applyFormatPreset,
} from './formatVariants';

/** Datasets the stress test runs a blueprint against. */
export type StressDataset = 'sparse' | 'typical' | 'dense' | 'noPhotos';

export type EditorTool = 'select' | 'hand' | 'frame' | 'text' | 'rect' | 'ellipse' | 'line' | 'image';
export type ZoomMode = 'fit' | 'manual' | 'actual';
export type LeftPanelTab = 'pages' | 'layers' | 'library' | 'assets';
export type InspectorMode = 'basic' | 'advanced';

export interface NamedCheckpoint {
  id: string;
  label: string;
  at: string;
  document: TemplateDocument;
}

interface EditorUiState {
  leftPanelTab: LeftPanelTab;
  leftPanelWidth: number;
  rightPanelWidth: number;
  leftPanelCollapsed: boolean;
  rightPanelCollapsed: boolean;
  commandPaletteOpen: boolean;
  layersDockOpen: boolean;
  inspectorOpen: boolean;
  inspectorPinned: boolean;
  bookFlowOpen: boolean;
  zoomMode: ZoomMode;
  canvasZoomPulse: number;
  canvasZoomDirection: 'in' | 'out';
  tourStep: number;
  panning: boolean;
  /** Canvas preview: render nodes hidden by visibleIf expressions. */
  showHiddenOnCanvas: boolean;
  inspectorMode: InspectorMode;
  inspectorQuery: string;
  authoringWizardOpen: boolean;
  formatCompareOpen: boolean;
  bookFlowFilter: 'all' | 'issues';
  aiDesignOpen: boolean;
  /** Edit-with-AI dock (live theater on the current document). */
  aiEditOpen: boolean;
  /** Op currently spotlighted in the theater feed. */
  aiEditActiveOpId: string | null;
  /** Node ids to pulse on canvas while reviewing AI edits. */
  aiEditSpotlightNodeIds: string[];
  /** Session combo of accepted AI edit jobs (tab-local, mirrored for UI). */
  aiEditCombo: number;
}

interface EditorState {
  templateId: string | null;
  versionId: string | null;
  templateName: string;
  document: TemplateDocument | null;
  /** Selected format branch for preview and future variant editing. */
  activeVariantId: string | null;

  selectedBlueprintId: string | null;
  /** Primary selection — last node in selectedNodeIds. */
  selectedNodeId: NodeId | null;
  selectedNodeIds: NodeId[];
  /** Painted clone under the pointer — used to measure chrome, not the first DOM match. */
  selectedInstanceId: string | null;
  /** Overlay child index when a placed decoration is selected. */
  selectedOverlayChildIndex: number | null;
  hoveredNodeId: NodeId | null;
  /** Opens the canvas inline editor once the node is painted. */
  pendingInlineEditNodeId: NodeId | null;
  activeTool: EditorTool;
  /** Clipboard for copy/paste. */
  clipboard: Node | null;
  lockedNodeIds: NodeId[];

  /**
   * Snapshot stack.
   *
   * Immer gives every reducer result structural sharing with its predecessor,
   * so holding references to past documents costs only the nodes that actually
   * changed - far simpler than replaying patches and just as cheap.
   */
  past: TemplateDocument[];
  future: TemplateDocument[];

  dirty: boolean;
  /** Published versions cannot be mutated in the constructor. */
  readOnly: boolean;
  lastSavedAt: string | null;
  saving: boolean;
  /** Latest document validation issues from save/publish (empty when clear). */
  validationIssues: Array<{ path: string; message: string }>;

  /** Preview controls, not part of the saved document. */
  previewSeed: string;
  previewDataset: StressDataset;
  previewRestaurantId: string | null;
  previewLiveMenuId: string | null;
  previewPageIndex: number;
  stressTestOpen: boolean;
  pageSetupOpen: boolean;
  designSystemOpen: boolean;
  shortcutsOpen: boolean;
  saveRequestId: number;
  /** First-run constructor tour. */
  tourDismissed: boolean;
  coachBannerDismissed: boolean;
  locale: ConstructorLocale;
  /** Constructor chrome: dark canvas vs light studio. */
  appearance: ConstructorAppearance;
  referenceOverlay: { url: string; opacity: number } | null;
  ui: EditorUiState;
  checkpoints: NamedCheckpoint[];
}

const HISTORY_LIMIT = 60;

function lockKey(versionId: string | null): string | null {
  return versionId ? `mv-locked-nodes:${versionId}` : null;
}

function readLockedNodes(versionId: string | null): NodeId[] {
  const key = lockKey(versionId);
  if (!key || typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as NodeId[]) : [];
  } catch {
    return [];
  }
}

function writeLockedNodes(versionId: string | null, ids: NodeId[]): void {
  const key = lockKey(versionId);
  if (!key || typeof localStorage === 'undefined') return;
  localStorage.setItem(key, JSON.stringify(ids));
}

function libraryPresetsKey(versionId: string | null): string | null {
  return versionId ? `mv-library-presets:${versionId}` : null;
}

function readLibraryPresets(versionId: string | null): Array<{ id: string; label: string; node: Node }> {
  const key = libraryPresetsKey(versionId);
  if (!key || typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Array<{ id: string; label: string; node: Node }>) : [];
  } catch {
    return [];
  }
}

function clearLibraryPresets(versionId: string | null): void {
  const key = libraryPresetsKey(versionId);
  if (!key || typeof localStorage === 'undefined') return;
  localStorage.removeItem(key);
}

const initialState: EditorState = {
  templateId: null,
  versionId: null,
  templateName: '',
  document: null,
  activeVariantId: null,
  selectedBlueprintId: null,
  selectedNodeId: null,
  selectedNodeIds: [],
  selectedInstanceId: null,
  selectedOverlayChildIndex: null,
  hoveredNodeId: null,
  pendingInlineEditNodeId: null,
  activeTool: 'select',
  clipboard: null,
  lockedNodeIds: [],
  past: [],
  future: [],
  dirty: false,
  readOnly: false,
  lastSavedAt: null,
  saving: false,
  validationIssues: [],
  previewSeed: 'preview',
  previewDataset: 'typical',
  previewRestaurantId: null,
  previewLiveMenuId: null,
  previewPageIndex: 0,
  stressTestOpen: false,
  pageSetupOpen: false,
  designSystemOpen: false,
  shortcutsOpen: false,
  saveRequestId: 0,
  tourDismissed: typeof localStorage !== 'undefined' && localStorage.getItem('mv-constructor-tour') === '1',
  coachBannerDismissed:
    typeof localStorage !== 'undefined' && localStorage.getItem('mv-constructor-coach') === '1',
  locale: detectConstructorLocale(),
  appearance: detectConstructorAppearance(),
  referenceOverlay: null,
  checkpoints: [],
  ui: {
    leftPanelTab: 'layers',
    leftPanelWidth: 280,
    rightPanelWidth: 320,
    leftPanelCollapsed: false,
    rightPanelCollapsed: false,
    commandPaletteOpen: false,
    layersDockOpen: true,
    inspectorOpen: true,
    inspectorPinned: true,
    bookFlowOpen: false,
    zoomMode: 'fit',
    canvasZoomPulse: 0,
    canvasZoomDirection: 'in',
    tourStep: 0,
    panning: false,
    showHiddenOnCanvas: false,
    inspectorMode: 'basic',
    inspectorQuery: '',
    authoringWizardOpen: false,
    formatCompareOpen: false,
    bookFlowFilter: 'all',
    aiDesignOpen: false,
    aiEditOpen: false,
    aiEditActiveOpId: null,
    aiEditSpotlightNodeIds: [],
    aiEditCombo: 0,
  },
};

function syncPrimarySelection(state: EditorState): void {
  state.selectedNodeId = state.selectedNodeIds[state.selectedNodeIds.length - 1] ?? null;
}

function checkpoint(state: EditorState): boolean {
  if (state.readOnly || !state.document) return false;
  const snapshot = isDraft(state.document)
    ? (original(state.document) as TemplateDocument | undefined)
    : state.document;
  if (!snapshot) return false;
  state.past.push(snapshot);
  if (state.past.length > HISTORY_LIMIT) state.past.shift();
  state.future = [];
  state.dirty = true;
  return true;
}

function flattenAllIds(root: Node): NodeId[] {
  return flattenTree(root).map((row) => row.node.id);
}

function currentBlueprint(state: EditorState): PageBlueprint | undefined {
  const blueprints = currentBlueprints(state);
  return blueprints?.find((blueprint) => blueprint.id === state.selectedBlueprintId);
}

function currentBlueprints(state: EditorState): PageBlueprint[] {
  if (isFrozenDocument(state.document)) {
    return frozenPagesAsBlueprints(state.document.pages);
  }
  return currentVariant(state)?.blueprints ?? state.document?.blueprints ?? [];
}

function blueprintToFrozenPage(blueprint: PageBlueprint): FrozenPage {
  return {
    id: blueprint.id,
    title: blueprint.label,
    group: blueprint.group,
    root: blueprint.root,
    itemIds: [],
  };
}

function currentVariant(state: EditorState) {
  return state.document?.variants?.find((variant) => variant.id === state.activeVariantId);
}

function syncBlueprintToActiveVariant(state: EditorState, preferredGroup?: PageBlueprint['group']) {
  const blueprints = currentBlueprints(state);
  if (blueprints.some((blueprint) => blueprint.id === state.selectedBlueprintId)) return;
  const groupMatch = preferredGroup
    ? blueprints.find((blueprint) => blueprint.group === preferredGroup)
    : undefined;
  state.selectedBlueprintId = groupMatch?.id ?? blueprints[0]?.id ?? null;
  state.selectedNodeId = null;
  state.selectedNodeIds = [];
  state.selectedInstanceId = null;
  state.selectedOverlayChildIndex = null;
}

const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    loadDocument(
      state,
      action: PayloadAction<{
        templateId: string;
        versionId: string;
        templateName: string;
        document: TemplateDocument;
        lastSavedAt?: string | null;
        readOnly?: boolean;
      }>,
    ) {
      state.templateId = action.payload.templateId;
      state.versionId = action.payload.versionId;
      state.templateName = action.payload.templateName;
      state.document = migrateToFrames(action.payload.document);
      state.activeVariantId = state.document.variants?.[0]?.id ?? null;
      if (!state.document.libraryPresets) state.document.libraryPresets = [];
      const legacy = readLibraryPresets(action.payload.versionId);
      let importedPresets = false;
      if (legacy.length > 0 && state.document.libraryPresets.length === 0) {
        state.document.libraryPresets = legacy;
        importedPresets = true;
        clearLibraryPresets(action.payload.versionId);
      } else if (legacy.length > 0) {
        clearLibraryPresets(action.payload.versionId);
      }
      const initialBlueprints = currentBlueprints(state);
      state.selectedBlueprintId =
        initialBlueprints.find((blueprint) => blueprint.group === 'cover')?.id ??
        initialBlueprints[0]?.id ??
        null;
      state.selectedNodeId = null;
      state.selectedNodeIds = [];
      state.selectedInstanceId = null;
      state.selectedOverlayChildIndex = null;
      state.lockedNodeIds = readLockedNodes(action.payload.versionId);
      state.past = [];
      state.future = [];
      state.dirty = importedPresets;
      state.readOnly = Boolean(action.payload.readOnly);
      state.lastSavedAt = action.payload.lastSavedAt ?? new Date().toISOString();
      state.previewPageIndex = 0;
      state.checkpoints = [];
    },

    closeDocument() {
      return initialState;
    },

    restoreDocument(state, action: PayloadAction<TemplateDocument>) {
      state.document = migrateToFrames(action.payload);
      state.past = [];
      state.future = [];
      state.dirty = false;
      state.saving = false;
      state.validationIssues = [];
      state.activeVariantId = state.document.variants?.[0]?.id ?? null;
      syncBlueprintToActiveVariant(state);
    },

    /**
     * Replace the working document with an AI-generated (or refined) candidate.
     * Checkpoints first so Undo restores the pre-accept design.
     */
    applyGeneratedDocument(state, action: PayloadAction<TemplateDocument>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      state.document = migrateToFrames(action.payload);
      if (!state.document.libraryPresets) state.document.libraryPresets = [];
      state.activeVariantId = state.document.variants?.[0]?.id ?? null;
      syncBlueprintToActiveVariant(state, 'cover');
      state.validationIssues = [];
      state.previewPageIndex = 0;
    },

    // --- Selection --------------------------------------------------------

    selectBlueprint(state, action: PayloadAction<string>) {
      state.selectedBlueprintId = action.payload;
      state.selectedNodeIds = [];
      state.selectedNodeId = null;
      state.selectedInstanceId = null;
      state.selectedOverlayChildIndex = null;
    },
    selectNode(
      state,
      action: PayloadAction<{
        nodeId: NodeId | null;
        overlayChildIndex?: number | null;
        additive?: boolean;
        toggle?: boolean;
        instanceId?: string | null;
      }>,
    ) {
      const { nodeId, overlayChildIndex, additive, toggle, instanceId } = action.payload;
      if (!nodeId) {
        state.selectedNodeIds = [];
        state.selectedNodeId = null;
        state.selectedInstanceId = null;
        state.selectedOverlayChildIndex = null;
        if (!state.ui.inspectorPinned) state.ui.inspectorOpen = false;
        return;
      }
      if (toggle) {
        const index = state.selectedNodeIds.indexOf(nodeId);
        if (index >= 0) state.selectedNodeIds.splice(index, 1);
        else state.selectedNodeIds.push(nodeId);
      } else if (additive) {
        if (!state.selectedNodeIds.includes(nodeId)) state.selectedNodeIds.push(nodeId);
      } else {
        state.selectedNodeIds = [nodeId];
      }
      syncPrimarySelection(state);
      state.selectedInstanceId = instanceId ?? null;
      state.selectedOverlayChildIndex = overlayChildIndex ?? null;
      if (!state.ui.inspectorPinned) {
        state.ui.inspectorOpen = state.selectedNodeIds.length > 0;
      }
    },
    selectNodes(state, action: PayloadAction<NodeId[]>) {
      state.selectedNodeIds = action.payload;
      syncPrimarySelection(state);
      state.selectedInstanceId = null;
      state.selectedOverlayChildIndex = null;
    },
    clearSelection(state) {
      state.selectedNodeIds = [];
      state.selectedNodeId = null;
      state.selectedInstanceId = null;
      state.selectedOverlayChildIndex = null;
      if (!state.ui.inspectorPinned) state.ui.inspectorOpen = false;
    },
    selectAllNodes(state) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const ids = flattenAllIds(blueprint.root).filter((id) => id !== blueprint.root.id);
      state.selectedNodeIds = ids.length > 0 ? ids : [blueprint.root.id];
      syncPrimarySelection(state);
      state.selectedInstanceId = null;
      state.selectedOverlayChildIndex = null;
    },
    selectAllOverlayNodes(state) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const framed = collectFramedNodeRects(blueprint.root).map((entry) => entry.id);
      const ids = framed.length > 0 ? framed : flattenAllIds(blueprint.root).filter((id) => id !== blueprint.root.id);
      state.selectedNodeIds = ids;
      syncPrimarySelection(state);
      state.selectedInstanceId = null;
      state.selectedOverlayChildIndex = null;
    },
    setHoveredNode(state, action: PayloadAction<NodeId | null>) {
      state.hoveredNodeId = action.payload;
    },
    selectParent(state) {
      const blueprint = currentBlueprint(state);
      if (!blueprint || !state.selectedNodeId) return;
      const parent = findParentOf(blueprint.root, state.selectedNodeId);
      if (!parent || parent.id === blueprint.root.id) {
        state.selectedNodeIds = [];
        state.selectedNodeId = null;
        state.selectedOverlayChildIndex = null;
        return;
      }
      state.selectedNodeIds = [parent.id];
      state.selectedNodeId = parent.id;
      state.selectedOverlayChildIndex = null;
    },
    beginInlineEdit(state, action: PayloadAction<NodeId>) {
      state.pendingInlineEditNodeId = action.payload;
    },
    clearInlineEditRequest(state) {
      state.pendingInlineEditNodeId = null;
    },
    setActiveTool(state, action: PayloadAction<EditorTool>) {
      state.activeTool = action.payload;
    },

    // --- Node tree --------------------------------------------------------

    updateNode(state, action: PayloadAction<{ nodeId: NodeId; patch: Partial<Node> }>) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const node = findNodeIn(blueprint.root, action.payload.nodeId);
      if (!node) return;
      if (state.lockedNodeIds.includes(action.payload.nodeId)) return;

      if (!checkpoint(state)) return;
      Object.assign(node, action.payload.patch);
      // Cleared optional fields must be deleted — otherwise JSON/Mongo store `null`
      // and the layout engine crashes on `.slice` of a null length token.
      for (const [key, value] of Object.entries(action.payload.patch)) {
        if (value === undefined) {
          delete (node as unknown as Record<string, unknown>)[key];
        }
      }
    },

    /** Repair preview-breaking null lengths without changing the active variant. */
    repairDocumentLengths(state) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      state.document = migrateToFrames(state.document);
      state.dirty = true;
    },

    insertNode(state, action: PayloadAction<{ parentId: NodeId; node: Node; index?: number }>) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;

      if (!checkpoint(state)) return;
      const inserted = insertNodeInto(
        blueprint.root,
        action.payload.parentId,
        action.payload.node,
        action.payload.index,
      );
      if (inserted) {
        state.selectedNodeIds = [action.payload.node.id];
        state.selectedNodeId = action.payload.node.id;
      }
    },

    updateOverlayChild(
      state,
      action: PayloadAction<{
        overlayId: NodeId;
        childIndex: number;
        patch: Partial<import('@menuvera/engine').OverlayChild>;
      }>,
    ) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const overlay = findNodeIn(blueprint.root, action.payload.overlayId);
      if (!overlay || overlay.kind !== 'overlay') return;
      if (state.lockedNodeIds.includes(action.payload.overlayId)) return;
      const child = overlay.children[action.payload.childIndex];
      if (!child) return;

      if (!checkpoint(state)) return;
      Object.assign(child, action.payload.patch);
    },

    setOverlayChildGeometry(
      state,
      action: PayloadAction<{
        overlayId: NodeId;
        childIndex: number;
        x: number;
        y: number;
        width: number;
        height: number;
        rotate?: number;
      }>,
    ) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const overlay = findNodeIn(blueprint.root, action.payload.overlayId);
      if (!overlay || overlay.kind !== 'overlay') return;
      const child = overlay.children[action.payload.childIndex];
      if (!child) return;
      if (
        state.lockedNodeIds.includes(action.payload.overlayId) ||
        state.lockedNodeIds.includes(child.node.id)
      ) return;

      if (!checkpoint(state)) return;
      child.x = action.payload.x;
      child.y = action.payload.y;
      child.width = action.payload.width;
      child.height = action.payload.height;
      if (action.payload.rotate !== undefined) child.rotate = action.payload.rotate;
    },

    addOverlayChild(
      state,
      action: PayloadAction<{ overlayId: NodeId; child: import('@menuvera/engine').OverlayChild }>,
    ) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const overlay = findNodeIn(blueprint.root, action.payload.overlayId);
      if (!overlay || overlay.kind !== 'overlay') return;

      if (!checkpoint(state)) return;
      overlay.children.push(action.payload.child);
      state.selectedNodeIds = [action.payload.child.node.id];
      state.selectedNodeId = action.payload.child.node.id;
      state.selectedOverlayChildIndex = overlay.children.length - 1;
    },

    removeOverlayChild(state, action: PayloadAction<{ overlayId: NodeId; childIndex: number }>) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const overlay = findNodeIn(blueprint.root, action.payload.overlayId);
      if (!overlay || overlay.kind !== 'overlay') return;

      if (!checkpoint(state)) return;
      overlay.children.splice(action.payload.childIndex, 1);
      state.selectedNodeIds = [];
      state.selectedNodeId = null;
      state.selectedOverlayChildIndex = null;
    },

    removeNode(state, action: PayloadAction<NodeId>) {
      const blueprint = currentBlueprint(state);
      if (!blueprint || blueprint.root.id === action.payload) return;
      if (state.lockedNodeIds.includes(action.payload)) return;

      if (!checkpoint(state)) return;
      removeNodeFrom(blueprint.root, action.payload);
      state.selectedNodeIds = state.selectedNodeIds.filter((id) => id !== action.payload);
      syncPrimarySelection(state);
      if (state.selectedNodeIds.length === 0) state.selectedOverlayChildIndex = null;
    },

    removeSelectedNodes(state) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const ids = state.selectedNodeIds.filter(
        (id) => id !== blueprint.root.id && !state.lockedNodeIds.includes(id),
      );
      if (ids.length === 0) return;
      if (!checkpoint(state)) return;
      for (const id of ids) removeNodeFrom(blueprint.root, id);
      state.selectedNodeIds = [];
      syncPrimarySelection(state);
      state.selectedOverlayChildIndex = null;
    },

    duplicateSelectedNodes(state) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const ids = state.selectedNodeIds.filter((id) => id !== blueprint.root.id);
      if (ids.length === 0) return;
      if (!checkpoint(state)) return;
      const created: NodeId[] = [];
      for (const id of ids) {
        const node = findNodeIn(blueprint.root, id);
        if (!node) continue;
        const copy = cloneNodeWithNewIds(node);
        const parent = findParentOf(blueprint.root, id);
        if (
          parent &&
          (parent.kind === 'stack' ||
            parent.kind === 'grid' ||
            parent.kind === 'group' ||
            parent.kind === 'frame')
        ) {
          const index = parent.children.findIndex((child) => child.id === id);
          parent.children.splice(index + 1, 0, copy);
          created.push(copy.id);
        } else if (parent?.kind === 'overlay') {
          const index = parent.children.findIndex((child) => child.node.id === id);
          if (index >= 0) {
            const source = parent.children[index]!;
            parent.children.splice(index + 1, 0, {
              ...source,
              node: copy,
            });
            created.push(copy.id);
          }
        }
      }
      if (created.length > 0) {
        state.selectedNodeIds = created;
        syncPrimarySelection(state);
      }
    },

    copySelectedNodes(state) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const nodes = state.selectedNodeIds
        .map((id) => findNodeIn(blueprint.root, id))
        .filter((node): node is Node => Boolean(node));
      if (nodes.length === 0) return;
      state.clipboard =
        nodes.length === 1
          ? cloneNodeWithNewIds(nodes[0]!)
          : {
              id: 'clip',
              kind: 'group',
              children: nodes.map((node) => cloneNodeWithNewIds(node)),
            };
    },

    cutSelectedNodes(state) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const nodes = state.selectedNodeIds
        .map((id) => findNodeIn(blueprint.root, id))
        .filter((node): node is Node => Boolean(node));
      if (nodes.length === 0) return;
      state.clipboard =
        nodes.length === 1
          ? cloneNodeWithNewIds(nodes[0]!)
          : {
              id: 'clip',
              kind: 'group',
              children: nodes.map((node) => cloneNodeWithNewIds(node)),
            };
      const ids = state.selectedNodeIds.filter(
        (id) => id !== blueprint.root.id && !state.lockedNodeIds.includes(id),
      );
      if (ids.length === 0) return;
      if (!checkpoint(state)) return;
      for (const id of ids) removeNodeFrom(blueprint.root, id);
      state.selectedNodeIds = [];
      syncPrimarySelection(state);
      state.selectedOverlayChildIndex = null;
    },

    groupSelectedNodes(state) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const ids = state.selectedNodeIds.filter((id) => id !== blueprint.root.id);
      if (ids.length < 1) return;
      const parent = findParentOf(blueprint.root, ids[0]!);
      if (
        !parent ||
        !(parent.kind === 'stack' || parent.kind === 'grid' || parent.kind === 'group' || parent.kind === 'frame')
      ) {
        return;
      }
      const siblingIds = ids.filter((id) => parent.children.some((child) => child.id === id));
      const indexes = siblingIds
        .map((id) => parent.children.findIndex((child) => child.id === id))
        .filter((index) => index >= 0)
        .sort((a, b) => a - b);
      if (indexes.length === 0) return;
      if (!checkpoint(state)) return;
      const picked = indexes.map((index) => parent.children[index]!);
      parent.children = parent.children.filter((child) => !siblingIds.includes(child.id));
      const group: Node = {
        id: newNodeId('grp'),
        kind: 'group',
        children: picked,
      };
      parent.children.splice(Math.min(indexes[0]!, parent.children.length), 0, group);
      state.selectedNodeIds = [group.id];
      syncPrimarySelection(state);
    },

    ungroupSelectedNode(state) {
      const blueprint = currentBlueprint(state);
      if (!blueprint || !state.selectedNodeId) return;
      const node = findNodeIn(blueprint.root, state.selectedNodeId);
      if (!node || node.kind !== 'group') return;
      const parent = findParentOf(blueprint.root, node.id);
      if (
        !parent ||
        !(parent.kind === 'stack' || parent.kind === 'grid' || parent.kind === 'group' || parent.kind === 'frame')
      ) {
        return;
      }
      if (!checkpoint(state)) return;
      const index = parent.children.findIndex((child) => child.id === node.id);
      parent.children.splice(index, 1, ...node.children);
      state.selectedNodeIds = node.children.map((child) => child.id);
      syncPrimarySelection(state);
    },

    duplicateNode(state, action: PayloadAction<NodeId>) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const node = findNodeIn(blueprint.root, action.payload);
      if (!node || node.id === blueprint.root.id) return;

      if (!checkpoint(state)) return;
      const copy = cloneNodeWithNewIds(node);
      // Placed straight after the original so the duplicate appears where the
      // author is already looking.
      const parent = findParentOf(blueprint.root, action.payload);
      if (
        parent &&
        (parent.kind === 'stack' || parent.kind === 'grid' || parent.kind === 'group' || parent.kind === 'frame')
      ) {
        const index = parent.children.findIndex((child) => child.id === action.payload);
        parent.children.splice(index + 1, 0, copy);
        state.selectedNodeIds = [copy.id];
        state.selectedNodeId = copy.id;
      }
    },

    copyNode(state, action: PayloadAction<NodeId>) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const node = findNodeIn(blueprint.root, action.payload);
      if (!node) return;
      state.clipboard = cloneNodeWithNewIds(node);
    },

    pasteNode(state) {
      const blueprint = currentBlueprint(state);
      if (!blueprint || !state.clipboard) return;
      const copy = cloneNodeWithNewIds(state.clipboard);
      const parentId = state.selectedNodeId
        ? findParentOf(blueprint.root, state.selectedNodeId)?.id ?? blueprint.root.id
        : blueprint.root.id;
      if (!checkpoint(state)) return;
      const inserted = insertNodeInto(blueprint.root, parentId, copy);
      if (inserted) {
        state.selectedNodeIds = [copy.id];
        state.selectedNodeId = copy.id;
      }
    },

    pasteFromJson(state, action: PayloadAction<Node>) {
      state.clipboard = cloneNodeWithNewIds(action.payload);
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const copy = cloneNodeWithNewIds(state.clipboard);
      const parentId = state.selectedNodeId
        ? findParentOf(blueprint.root, state.selectedNodeId)?.id ?? blueprint.root.id
        : blueprint.root.id;
      if (!checkpoint(state)) return;
      if (insertNodeInto(blueprint.root, parentId, copy)) {
        state.selectedNodeIds = [copy.id];
        state.selectedNodeId = copy.id;
      }
    },

    moveNode(
      state,
      action: PayloadAction<{ nodeId: NodeId; targetParentId: NodeId; index: number }>,
    ) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;

      const { nodeId, targetParentId, index } = action.payload;
      if (nodeId === targetParentId) return;
      // Dropping a container inside itself would detach the whole subtree.
      if (isDescendant(blueprint.root, nodeId, targetParentId)) return;

      if (!checkpoint(state)) return;
      const detached = removeNodeFrom(blueprint.root, nodeId);
      if (!detached) return;
      insertNodeInto(blueprint.root, targetParentId, detached, index);
    },

    // --- Blueprints -------------------------------------------------------

    addBlueprint(state, action: PayloadAction<PageBlueprint>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      if (isFrozenDocument(state.document)) {
        state.document.pages.push(blueprintToFrozenPage(action.payload));
      } else {
        currentBlueprints(state).push(action.payload);
      }
      state.selectedBlueprintId = action.payload.id;
      state.selectedNodeIds = [];
      state.selectedNodeId = null;
    },

    fillMissingBookPages(state) {
      if (!state.document || isFrozenDocument(state.document)) return;
      if (missingBookGroups(state.document, state.activeVariantId).length === 0) return;
      if (!checkpoint(state)) return;
      fillEmptyBookPages(state.document, state.activeVariantId);
      const target = currentVariant(state);
      Object.assign(target?.flow ?? state.document.flow, {
        includeCover: true,
        includeBackCover: true,
        includeTableOfContents: true,
        includeInfoPage: true,
        dividers: state.document.flow.dividers === 'none' ? 'auto' : state.document.flow.dividers,
        varietyWindow: Math.max(state.document.flow.varietyWindow, 3),
        allowSpotlights: true,
      });
      state.selectedBlueprintId =
        currentBlueprints(state).find((blueprint) => blueprint.group === 'cover')?.id ??
        state.selectedBlueprintId;
    },

    clearCurrentPage: {
      reducer(state, action: PayloadAction<string | undefined>) {
        const id = action.payload ?? state.selectedBlueprintId;
        const blueprint = currentBlueprints(state).find((entry) => entry.id === id);
        if (!blueprint || !acceptsChildren(blueprint.root)) return;
        if (!checkpoint(state)) return;
        blueprint.root.children = [];
        if (state.selectedBlueprintId === blueprint.id) {
          state.selectedNodeIds = [];
          state.selectedNodeId = null;
        }
      },
      prepare(blueprintId?: string) {
        return { payload: blueprintId };
      },
    },

    clearAllPages(state) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      for (const blueprint of currentBlueprints(state)) {
        if (acceptsChildren(blueprint.root)) blueprint.root.children = [];
      }
      state.selectedNodeIds = [];
      state.selectedNodeId = null;
    },

    updateBlueprint(state, action: PayloadAction<{ id: string; patch: Partial<PageBlueprint> }>) {
      if (isFrozenDocument(state.document)) {
        const page = state.document.pages.find((entry) => entry.id === action.payload.id);
        if (!page) return;
        if (!checkpoint(state)) return;
        const { patch } = action.payload;
        if (patch.label !== undefined) page.title = patch.label;
        if (patch.group !== undefined) page.group = patch.group;
        if (patch.root !== undefined) page.root = patch.root;
        return;
      }
      const blueprint = currentBlueprints(state).find((entry) => entry.id === action.payload.id);
      if (!blueprint) return;
      if (!checkpoint(state)) return;
      Object.assign(blueprint, action.payload.patch);
    },

    duplicateBlueprint(state, action: PayloadAction<string>) {
      if (!state.document) return;
      if (isFrozenDocument(state.document)) {
        const source = state.document.pages.find((entry) => entry.id === action.payload);
        if (!source) return;
        if (!checkpoint(state)) return;
        const copy: FrozenPage = {
          ...structuredClone(source),
          id: newNodeId('page'),
          title: `${source.title ?? 'Page'} copy`,
          root: cloneNodeWithNewIds(source.root),
        };
        state.document.pages.push(copy);
        state.selectedBlueprintId = copy.id;
        state.selectedNodeIds = [];
        state.selectedNodeId = null;
        return;
      }
      const blueprints = currentBlueprints(state);
      const source = blueprints.find((entry) => entry.id === action.payload);
      if (!source) return;

      if (!checkpoint(state)) return;
      const copy = makeBlueprintCopy(source);
      blueprints.push(copy);
      state.selectedBlueprintId = copy.id;
      state.selectedNodeIds = [];
      state.selectedNodeId = null;
    },

    removeBlueprint(state, action: PayloadAction<string>) {
      if (!state.document) return;
      if (isFrozenDocument(state.document)) {
        if (state.document.pages.length <= 1) return;
        if (!checkpoint(state)) return;
        state.document.pages = state.document.pages.filter(
          (entry) => entry.id !== action.payload,
        );
        if (state.selectedBlueprintId === action.payload) {
          state.selectedBlueprintId = state.document.pages[0]?.id ?? null;
          state.selectedNodeIds = [];
          state.selectedNodeId = null;
        }
        return;
      }
      const blueprints = currentBlueprints(state);
      const remaining = blueprints.filter((entry) => entry.id !== action.payload);
      // A group with no variants left would make every page of that kind fail
      // to lay out, so the last one is not deletable.
      const group = blueprints.find((entry) => entry.id === action.payload)?.group;
      if (group && !remaining.some((entry) => entry.group === group)) return;

      if (!checkpoint(state)) return;
      if (currentVariant(state)) currentVariant(state)!.blueprints = remaining;
      else state.document.blueprints = remaining;
      if (state.selectedBlueprintId === action.payload) {
        state.selectedBlueprintId = remaining[0]?.id ?? null;
        state.selectedNodeIds = [];
        state.selectedNodeId = null;
      }
    },

    reorderFrozenPage(
      state,
      action: PayloadAction<{ id: string; direction: 'up' | 'down' }>,
    ) {
      if (!isFrozenDocument(state.document)) return;
      const pages = state.document.pages;
      const index = pages.findIndex((entry) => entry.id === action.payload.id);
      if (index < 0) return;
      const target = action.payload.direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= pages.length) return;
      if (!checkpoint(state)) return;
      const [page] = pages.splice(index, 1);
      pages.splice(target, 0, page!);
    },

    reorderOverlayChild(
      state,
      action: PayloadAction<{ overlayId: NodeId; childIndex: number; direction: 'forward' | 'backward' }>,
    ) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const overlay = findNodeIn(blueprint.root, action.payload.overlayId);
      if (!overlay || overlay.kind !== 'overlay') return;
      const { childIndex, direction } = action.payload;
      const swapIndex = direction === 'forward' ? childIndex + 1 : childIndex - 1;
      if (swapIndex < 0 || swapIndex >= overlay.children.length) return;
      if (!checkpoint(state)) return;
      const temp = overlay.children[childIndex]!;
      overlay.children[childIndex] = overlay.children[swapIndex]!;
      overlay.children[swapIndex] = temp;
    },

    nudgeOverlayChild(
      state,
      action: PayloadAction<{
        overlayId: NodeId;
        childIndex: number;
        dx: number;
        dy: number;
      }>,
    ) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const overlay = findNodeIn(blueprint.root, action.payload.overlayId);
      if (!overlay || overlay.kind !== 'overlay') return;
      const child = overlay.children[action.payload.childIndex];
      if (!child) return;
      if (!checkpoint(state)) return;
      child.x = Math.max(0, Math.min(1 - child.width, child.x + action.payload.dx));
      child.y = Math.max(0, Math.min(1 - child.height, child.y + action.payload.dy));
    },

    detachNodeToOverlay(
      state,
      action: PayloadAction<{
        nodeId: NodeId;
        x: number;
        y: number;
        width: number;
        height: number;
      }>,
    ) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const node = findNodeIn(blueprint.root, action.payload.nodeId);
      if (!node) return;
      if (state.lockedNodeIds.includes(action.payload.nodeId)) return;
      if (!checkpoint(state)) return;
      node.frame = {
        x: action.payload.x,
        y: action.payload.y,
        width: action.payload.width,
        height: action.payload.height,
        z: (node.frame?.z ?? 1),
      };
      state.selectedNodeIds = [action.payload.nodeId];
      state.selectedNodeId = action.payload.nodeId;
      state.selectedOverlayChildIndex = null;
    },

    setNodeFrame(
      state,
      action: PayloadAction<{ nodeId: NodeId; frame: Partial<NodeFrame> }>,
    ) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const node = findNodeIn(blueprint.root, action.payload.nodeId);
      if (!node) return;
      if (state.lockedNodeIds.includes(action.payload.nodeId)) return;
      if (!checkpoint(state)) return;
      const base: NodeFrame = node.frame ?? {
        x: 0,
        y: 0,
        width: 0.2,
        height: 0.2,
        z: 1,
      };
      node.frame = { ...base, ...action.payload.frame };
      syncLegacyOverlayChild(blueprint.root, node.id, node.frame);
    },

    setNodeFrames(
      state,
      action: PayloadAction<Array<{ nodeId: NodeId; frame: Partial<NodeFrame> }>>,
    ) {
      const blueprint = currentBlueprint(state);
      if (!blueprint || action.payload.length === 0) return;
      const entries = action.payload.filter((entry) => !state.lockedNodeIds.includes(entry.nodeId));
      if (entries.length === 0 || !checkpoint(state)) return;
      for (const entry of entries) {
        const node = findNodeIn(blueprint.root, entry.nodeId);
        if (!node) continue;
        const base: NodeFrame = node.frame ?? {
          x: 0,
          y: 0,
          width: 0.2,
          height: 0.2,
          z: 1,
        };
        node.frame = { ...base, ...entry.frame };
        syncLegacyOverlayChild(blueprint.root, node.id, node.frame);
      }
    },

    nudgeNodeFrame(
      state,
      action: PayloadAction<{ nodeId: NodeId; dx: number; dy: number }>,
    ) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const node = findNodeIn(blueprint.root, action.payload.nodeId);
      if (!node?.frame) return;
      if (state.lockedNodeIds.includes(action.payload.nodeId)) return;
      if (!checkpoint(state)) return;
      node.frame.x = Math.max(0, Math.min(1 - node.frame.width, node.frame.x + action.payload.dx));
      node.frame.y = Math.max(0, Math.min(1 - node.frame.height, node.frame.y + action.payload.dy));
      syncLegacyOverlayChild(blueprint.root, node.id, node.frame);
    },

    nudgeNodeFrames(
      state,
      action: PayloadAction<{ nodeIds: NodeId[]; dx: number; dy: number }>,
    ) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      if (!checkpoint(state)) return;
      for (const nodeId of action.payload.nodeIds) {
        const node = findNodeIn(blueprint.root, nodeId);
        if (!node?.frame) continue;
        if (state.lockedNodeIds.includes(nodeId)) continue;
        node.frame.x = Math.max(0, Math.min(1 - node.frame.width, node.frame.x + action.payload.dx));
        node.frame.y = Math.max(0, Math.min(1 - node.frame.height, node.frame.y + action.payload.dy));
        syncLegacyOverlayChild(blueprint.root, node.id, node.frame);
      }
    },

    reorderNodeZ(
      state,
      action: PayloadAction<{ nodeId: NodeId; direction: 'forward' | 'backward' }>,
    ) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const node = findNodeIn(blueprint.root, action.payload.nodeId);
      if (!node?.frame) return;
      if (state.lockedNodeIds.includes(action.payload.nodeId)) return;
      if (!checkpoint(state)) return;
      const delta = action.payload.direction === 'forward' ? 1 : -1;
      node.frame.z = Math.max(1, (node.frame.z ?? 1) + delta);
      syncLegacyOverlayChild(blueprint.root, node.id, node.frame);
    },

    alignNodes(
      state,
      action: PayloadAction<{
        nodeIds: NodeId[];
        axis: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom';
      }>,
    ) {
      const blueprint = currentBlueprint(state);
      if (!blueprint || action.payload.nodeIds.length < 2) return;
      const frames = action.payload.nodeIds
        .map((id) => {
          const node = findNodeIn(blueprint.root, id);
          return node?.frame ? { id, frame: node.frame } : null;
        })
        .filter((entry): entry is { id: NodeId; frame: NodeFrame } => entry !== null);
      if (frames.length < 2) return;
      const editable = frames.filter((entry) => !state.lockedNodeIds.includes(entry.id));
      if (editable.length < 2) return;
      if (!checkpoint(state)) return;

      const apply = (id: NodeId, patch: Partial<NodeFrame>) => {
        const node = findNodeIn(blueprint.root, id);
        if (!node?.frame) return;
        Object.assign(node.frame, patch);
        syncLegacyOverlayChild(blueprint.root, id, node.frame);
      };

      const { axis } = action.payload;
      if (axis === 'left') {
        const minX = Math.min(...frames.map((entry) => entry.frame.x));
        editable.forEach((entry) => apply(entry.id, { x: minX }));
      } else if (axis === 'right') {
        const maxRight = Math.max(...frames.map((entry) => entry.frame.x + entry.frame.width));
        editable.forEach((entry) => apply(entry.id, { x: maxRight - entry.frame.width }));
      } else if (axis === 'center') {
        const centers = frames.map((entry) => entry.frame.x + entry.frame.width / 2);
        const avg = centers.reduce((sum, value) => sum + value, 0) / centers.length;
        editable.forEach((entry) => apply(entry.id, { x: avg - entry.frame.width / 2 }));
      } else if (axis === 'top') {
        const minY = Math.min(...frames.map((entry) => entry.frame.y));
        editable.forEach((entry) => apply(entry.id, { y: minY }));
      } else if (axis === 'bottom') {
        const maxBottom = Math.max(...frames.map((entry) => entry.frame.y + entry.frame.height));
        editable.forEach((entry) => apply(entry.id, { y: maxBottom - entry.frame.height }));
      } else if (axis === 'middle') {
        const centers = frames.map((entry) => entry.frame.y + entry.frame.height / 2);
        const avg = centers.reduce((sum, value) => sum + value, 0) / centers.length;
        editable.forEach((entry) => apply(entry.id, { y: avg - entry.frame.height / 2 }));
      }
    },

    distributeNodes(
      state,
      action: PayloadAction<{ nodeIds: NodeId[]; axis: 'x' | 'y' }>,
    ) {
      const blueprint = currentBlueprint(state);
      if (!blueprint || action.payload.nodeIds.length < 3) return;
      const frames = action.payload.nodeIds
        .map((id) => {
          const node = findNodeIn(blueprint.root, id);
          return node?.frame ? { id, frame: node.frame } : null;
        })
        .filter((entry): entry is { id: NodeId; frame: NodeFrame } => entry !== null);
      if (frames.length < 3) return;
      const editable = frames.filter((entry) => !state.lockedNodeIds.includes(entry.id));
      if (editable.length < 2) return;
      if (!checkpoint(state)) return;

      const sorted = [...frames].sort((left, right) =>
        action.payload.axis === 'x'
          ? left.frame.x - right.frame.x
          : left.frame.y - right.frame.y,
      );
      const first = sorted[0]!;
      const last = sorted[sorted.length - 1]!;
      const span =
        action.payload.axis === 'x'
          ? last.frame.x + last.frame.width - first.frame.x
          : last.frame.y + last.frame.height - first.frame.y;
      const totalSize = sorted.reduce(
        (sum, entry) => sum + (action.payload.axis === 'x' ? entry.frame.width : entry.frame.height),
        0,
      );
      const gap = (span - totalSize) / (sorted.length - 1);
      let cursor = action.payload.axis === 'x' ? first.frame.x : first.frame.y;

      sorted.forEach((entry, index) => {
        if (index === 0 || index === sorted.length - 1 || state.lockedNodeIds.includes(entry.id)) return;
        const node = findNodeIn(blueprint.root, entry.id);
        if (!node?.frame) return;
        if (action.payload.axis === 'x') {
          node.frame.x = cursor;
          cursor += node.frame.width + gap;
        } else {
          node.frame.y = cursor;
          cursor += node.frame.height + gap;
        }
        syncLegacyOverlayChild(blueprint.root, entry.id, node.frame);
      });
    },

    insertFramedNode(
      state,
      action: PayloadAction<{ node: Node; frame: NodeFrame; parentId?: NodeId }>,
    ) {
      const blueprint = currentBlueprint(state);
      if (!blueprint) return;
      const parentId = action.payload.parentId ?? blueprint.root.id;
      if (!checkpoint(state)) return;
      const node = { ...action.payload.node, frame: action.payload.frame };
      const inserted = insertNodeInto(blueprint.root, parentId, node);
      if (inserted) {
        state.selectedNodeIds = [node.id];
        state.selectedNodeId = node.id;
        state.selectedOverlayChildIndex = null;
      }
    },

    toggleNodeLock(state, action: PayloadAction<NodeId>) {
      if (state.readOnly) return;
      const index = state.lockedNodeIds.indexOf(action.payload);
      if (index >= 0) state.lockedNodeIds.splice(index, 1);
      else state.lockedNodeIds.push(action.payload);
      writeLockedNodes(state.versionId, state.lockedNodeIds);
    },

    // --- Tokens and settings ---------------------------------------------

    setColorToken(state, action: PayloadAction<{ key: string; value: string }>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      state.document.tokens.colors[action.payload.key] = action.payload.value;
    },

    removeColorToken(state, action: PayloadAction<string>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      delete state.document.tokens.colors[action.payload];
    },

    setTypographyToken(state, action: PayloadAction<{ key: string; value: TypographyToken }>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      state.document.tokens.typography[action.payload.key] = action.payload.value;
    },

    setSpacingToken(state, action: PayloadAction<{ key: string; value: number }>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      state.document.tokens.spacing[action.payload.key] = action.payload.value;
    },

    setRadiusToken(state, action: PayloadAction<{ key: string; value: number }>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      state.document.tokens.radii[action.payload.key] = action.payload.value;
    },

    updateFonts(state, action: PayloadAction<Partial<DesignTokens['fonts']>>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      Object.assign(state.document.tokens.fonts, action.payload);
    },

    updateCanvas(state, action: PayloadAction<Partial<CanvasSpec>>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      const variant = currentVariant(state);
      Object.assign(variant?.canvas ?? state.document.canvas, action.payload);
    },

    updateFlow(state, action: PayloadAction<Partial<FlowRules>>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      const variant = currentVariant(state);
      Object.assign(variant?.flow ?? state.document.flow, action.payload);
    },

    setFormat(state, action: PayloadAction<MenuFormat>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      const variant = currentVariant(state);
      if (variant) {
        applyFormatPreset(variant, action.payload, variant.format);
      } else {
        applyFormatPreset(state.document, action.payload, state.document.format);
      }
    },

    setActiveVariant(state, action: PayloadAction<string | null>) {
      const previousGroup = currentBlueprint(state)?.group;
      state.activeVariantId = action.payload;
      syncBlueprintToActiveVariant(state, previousGroup);
    },

    addFormatVariant: {
      reducer(state, action: PayloadAction<MenuFormat | undefined>) {
        if (!state.document) return;
        if (!checkpoint(state)) return;
        state.document.variants = ensureExplicitVariants(state.document);
        const variant = buildAddedFormatVariant(
          state.document,
          state.activeVariantId,
          action.payload,
        );
        if (state.document.variants.some((entry) => entry.id === variant.id)) return;
        state.document.variants.push(variant);
        state.activeVariantId = variant.id;
        state.selectedBlueprintId = variant.blueprints[0]?.id ?? null;
        state.selectedNodeId = null;
        state.selectedNodeIds = [];
      },
      prepare(format?: MenuFormat) {
        return { payload: format };
      },
    },

    removeFormatVariant(state, action: PayloadAction<string>) {
      if (!state.document?.variants) return;
      if (!checkpoint(state)) return;
      state.document.variants = state.document.variants.filter(
        (variant) => variant.id !== action.payload,
      );
      if (state.activeVariantId === action.payload) {
        state.activeVariantId = state.document.variants[0]?.id ?? null;
        syncBlueprintToActiveVariant(state);
      }
    },

    setBorderToken(state, action: PayloadAction<{ key: string; value: BorderToken }>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      state.document.tokens.borders[action.payload.key] = action.payload.value;
    },

    removeBorderToken(state, action: PayloadAction<string>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      delete state.document.tokens.borders[action.payload];
    },

    setShadowToken(state, action: PayloadAction<{ key: string; value: ShadowToken }>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      state.document.tokens.shadows[action.payload.key] = action.payload.value;
    },

    removeShadowToken(state, action: PayloadAction<string>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      delete state.document.tokens.shadows[action.payload];
    },

    addContrastRule(state, action: PayloadAction<ContrastRule>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      state.document.contrastRules.push(action.payload);
    },

    removeContrastRule(state, action: PayloadAction<number>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      state.document.contrastRules.splice(action.payload, 1);
    },

    updateContrastRule(state, action: PayloadAction<{ index: number; value: ContrastRule }>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      state.document.contrastRules[action.payload.index] = action.payload.value;
    },

    addTemplateAsset(
      state,
      action: PayloadAction<import('@menuvera/engine').TemplateAsset>,
    ) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      state.document.assets.push(action.payload);
    },

    updateTemplateAsset(
      state,
      action: PayloadAction<{ id: string; label?: string }>,
    ) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      const asset = state.document.assets.find((entry) => entry.id === action.payload.id);
      if (asset && action.payload.label !== undefined) asset.label = action.payload.label;
    },

    removeTemplateAsset(state, action: PayloadAction<string>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      scrubAssetFromDocument(state.document, action.payload);
      state.document.assets = state.document.assets.filter((entry) => entry.id !== action.payload);
    },

    saveLibraryPreset(state, action: PayloadAction<{ label: string; node: Node }>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      if (!state.document.libraryPresets) state.document.libraryPresets = [];
      state.document.libraryPresets.push({
        id: `preset-${Date.now().toString(36)}`,
        label: action.payload.label,
        node: cloneNodeWithNewIds(action.payload.node),
      });
    },

    removeLibraryPreset(state, action: PayloadAction<string>) {
      if (!state.document?.libraryPresets) return;
      if (!checkpoint(state)) return;
      state.document.libraryPresets = state.document.libraryPresets.filter(
        (entry) => entry.id !== action.payload,
      );
    },

    updateBrandable(state, action: PayloadAction<Partial<BrandableTokens>>) {
      if (!state.document) return;
      if (!checkpoint(state)) return;
      Object.assign(state.document.brandable, action.payload);
    },

    // --- History ----------------------------------------------------------

    undo(state) {
      if (state.readOnly) return;
      const previous = state.past.pop();
      if (!previous || !state.document) return;
      const current = isDraft(state.document)
        ? (original(state.document) as TemplateDocument | undefined)
        : state.document;
      if (current) state.future.unshift(current);
      state.document = previous;
      state.dirty = true;
      // Document history does not store selection — drop orphan blueprint/node ids.
      syncBlueprintToActiveVariant(state);
    },

    redo(state) {
      if (state.readOnly) return;
      const next = state.future.shift();
      if (!next || !state.document) return;
      const current = isDraft(state.document)
        ? (original(state.document) as TemplateDocument | undefined)
        : state.document;
      if (current) state.past.push(current);
      state.document = next;
      state.dirty = true;
      syncBlueprintToActiveVariant(state);
    },

    // --- Persistence ------------------------------------------------------

    requestSave(state) {
      state.saveRequestId += 1;
    },
    setSaving(state, action: PayloadAction<boolean>) {
      state.saving = action.payload;
    },
    markSaved(state) {
      state.dirty = false;
      state.saving = false;
      state.lastSavedAt = new Date().toISOString();
    },
    setValidationIssues(
      state,
      action: PayloadAction<Array<{ path: string; message: string }>>,
    ) {
      state.validationIssues = action.payload;
    },

    // --- Preview ----------------------------------------------------------

    setPreviewSeed(state, action: PayloadAction<string>) {
      state.previewSeed = action.payload;
    },
    setPreviewDataset(state, action: PayloadAction<StressDataset>) {
      state.previewDataset = action.payload;
      state.previewLiveMenuId = null;
    },
    setPreviewRestaurantId(state, action: PayloadAction<string | null>) {
      state.previewRestaurantId = action.payload;
      state.previewLiveMenuId = null;
    },
    setPreviewLiveMenuId(state, action: PayloadAction<string | null>) {
      state.previewLiveMenuId = action.payload;
    },
    setPreviewPageIndex(state, action: PayloadAction<number>) {
      state.previewPageIndex = action.payload;
    },
    setStressTestOpen(state, action: PayloadAction<boolean>) {
      if (action.payload) closeConstructorOverlays(state);
      state.stressTestOpen = action.payload;
    },
    setPageSetupOpen(state, action: PayloadAction<boolean>) {
      if (action.payload) closeConstructorOverlays(state);
      state.pageSetupOpen = action.payload;
    },
    setDesignSystemOpen(state, action: PayloadAction<boolean>) {
      if (action.payload) closeConstructorOverlays(state);
      state.designSystemOpen = action.payload;
    },
    setShortcutsOpen(state, action: PayloadAction<boolean>) {
      if (action.payload) closeConstructorOverlays(state);
      state.shortcutsOpen = action.payload;
    },
    closeAllOverlays(state) {
      closeConstructorOverlays(state);
    },
    setLayersDockOpen(state, action: PayloadAction<boolean>) {
      state.ui.layersDockOpen = action.payload;
    },
    toggleLayersDock(state) {
      state.ui.layersDockOpen = !state.ui.layersDockOpen;
    },
    setInspectorOpen(state, action: PayloadAction<boolean>) {
      state.ui.inspectorOpen = action.payload;
    },
    toggleInspectorPinned(state) {
      state.ui.inspectorPinned = !state.ui.inspectorPinned;
    },
    setBookFlowOpen(state, action: PayloadAction<boolean>) {
      if (action.payload) closeConstructorOverlays(state);
      state.ui.bookFlowOpen = action.payload;
    },
    setZoomMode(state, action: PayloadAction<ZoomMode>) {
      state.ui.zoomMode = action.payload;
    },
    nudgeCanvasZoom(state, action: PayloadAction<'in' | 'out'>) {
      state.ui.canvasZoomPulse += 1;
      state.ui.canvasZoomDirection = action.payload;
    },
    setPanning(state, action: PayloadAction<boolean>) {
      state.ui.panning = action.payload;
    },
    setShowHiddenOnCanvas(state, action: PayloadAction<boolean>) {
      state.ui.showHiddenOnCanvas = action.payload;
    },
    setTourStep(state, action: PayloadAction<number>) {
      state.ui.tourStep = action.payload;
    },
    dismissTour(state) {
      state.tourDismissed = true;
      state.ui.tourStep = 0;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('mv-constructor-tour', '1');
      }
    },
    dismissCoachBanner(state) {
      state.coachBannerDismissed = true;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('mv-constructor-coach', '1');
      }
    },
    setLocale(state, action: PayloadAction<ConstructorLocale>) {
      state.locale = action.payload;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(LOCALE_STORAGE_KEY, action.payload);
      }
    },
    setAppearance(state, action: PayloadAction<ConstructorAppearance>) {
      state.appearance = action.payload;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(APPEARANCE_STORAGE_KEY, action.payload);
      }
    },
    toggleAppearance(state) {
      const next: ConstructorAppearance = state.appearance === 'dark' ? 'light' : 'dark';
      state.appearance = next;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(APPEARANCE_STORAGE_KEY, next);
      }
    },
    setReferenceOverlay(state, action: PayloadAction<{ url: string; opacity: number } | null>) {
      state.referenceOverlay = action.payload;
    },
    setLeftPanelTab(state, action: PayloadAction<LeftPanelTab>) {
      state.ui.leftPanelTab = action.payload;
      state.ui.leftPanelCollapsed = false;
    },
    setLeftPanelWidth(state, action: PayloadAction<number>) {
      state.ui.leftPanelWidth = Math.max(200, Math.min(480, action.payload));
    },
    setRightPanelWidth(state, action: PayloadAction<number>) {
      state.ui.rightPanelWidth = Math.max(240, Math.min(520, action.payload));
    },
    toggleLeftPanelCollapsed(state) {
      state.ui.leftPanelCollapsed = !state.ui.leftPanelCollapsed;
    },
    toggleRightPanelCollapsed(state) {
      state.ui.rightPanelCollapsed = !state.ui.rightPanelCollapsed;
    },
    setCommandPaletteOpen(state, action: PayloadAction<boolean>) {
      state.ui.commandPaletteOpen = action.payload;
    },
    setInspectorMode(state, action: PayloadAction<InspectorMode>) {
      state.ui.inspectorMode = action.payload;
    },
    setInspectorQuery(state, action: PayloadAction<string>) {
      state.ui.inspectorQuery = action.payload;
    },
    setAuthoringWizardOpen(state, action: PayloadAction<boolean>) {
      if (action.payload) closeConstructorOverlays(state);
      state.ui.authoringWizardOpen = action.payload;
    },
    setAiDesignOpen(state, action: PayloadAction<boolean>) {
      if (action.payload) closeConstructorOverlays(state);
      state.ui.aiDesignOpen = action.payload;
    },
    setAiEditOpen(state, action: PayloadAction<boolean>) {
      if (action.payload) closeConstructorOverlays(state);
      state.ui.aiEditOpen = action.payload;
      if (action.payload) {
        state.ui.rightPanelCollapsed = true;
      }
      if (!action.payload) {
        state.ui.aiEditActiveOpId = null;
        state.ui.aiEditSpotlightNodeIds = [];
      }
    },
    setAiEditActiveOp(
      state,
      action: PayloadAction<{ opId: string | null; nodeIds?: string[] }>,
    ) {
      state.ui.aiEditActiveOpId = action.payload.opId;
      state.ui.aiEditSpotlightNodeIds = action.payload.nodeIds ?? [];
      if (action.payload.nodeIds?.length) {
        state.selectedNodeIds = action.payload.nodeIds.slice(0, 24);
        syncPrimarySelection(state);
      }
    },
    setAiEditCombo(state, action: PayloadAction<number>) {
      state.ui.aiEditCombo = Math.max(0, action.payload);
    },
    setFormatCompareOpen(state, action: PayloadAction<boolean>) {
      if (action.payload) closeConstructorOverlays(state);
      state.ui.formatCompareOpen = action.payload;
    },
    setBookFlowFilter(state, action: PayloadAction<'all' | 'issues'>) {
      state.ui.bookFlowFilter = action.payload;
    },
    saveNamedCheckpoint(state, action: PayloadAction<string | undefined>) {
      if (!state.document) return;
      const snapshot = isDraft(state.document)
        ? (original(state.document) as TemplateDocument | undefined)
        : state.document;
      if (!snapshot) return;
      state.checkpoints.push({
        id: `cp-${Date.now().toString(36)}`,
        label: action.payload || `Checkpoint ${state.checkpoints.length + 1}`,
        at: new Date().toISOString(),
        document: snapshot,
      });
      if (state.checkpoints.length > 8) state.checkpoints.shift();
    },
    restoreNamedCheckpoint(state, action: PayloadAction<string>) {
      const checkpointEntry = state.checkpoints.find((entry) => entry.id === action.payload);
      if (!checkpointEntry) return;
      state.document = checkpointEntry.document;
      state.dirty = true;
      state.past = [];
      state.future = [];
      syncBlueprintToActiveVariant(state);
    },
  },
});

/** Only one constructor overlay at a time — opening one dismisses the rest. */
function closeConstructorOverlays(state: EditorState): void {
  state.stressTestOpen = false;
  state.pageSetupOpen = false;
  state.designSystemOpen = false;
  state.shortcutsOpen = false;
  state.ui.bookFlowOpen = false;
  state.ui.authoringWizardOpen = false;
  state.ui.formatCompareOpen = false;
  state.ui.aiDesignOpen = false;
  state.ui.aiEditOpen = false;
  state.ui.aiEditActiveOpId = null;
  state.ui.aiEditSpotlightNodeIds = [];
}

function syncLegacyOverlayChild(root: Node, nodeId: NodeId, frame: NodeFrame): void {
  const placement = findOverlayPlacement(root, nodeId);
  if (!placement) return;
  placement.child.x = frame.x;
  placement.child.y = frame.y;
  placement.child.width = frame.width;
  placement.child.height = frame.height;
  placement.child.rotate = frame.rotate;
  placement.child.z = frame.z;
}

function findParentOf(root: Node, id: NodeId): Node | undefined {
  switch (root.kind) {
    case 'stack':
    case 'grid':
    case 'group':
    case 'frame': {
      if (root.children.some((child) => child.id === id)) return root;
      for (const child of root.children) {
        const found = findParentOf(child, id);
        if (found) return found;
      }
      break;
    }
    case 'overlay': {
      if (root.children.some((child) => child.node.id === id)) return root;
      for (const child of root.children) {
        const found = findParentOf(child.node, id);
        if (found) return found;
      }
      break;
    }
    case 'repeater': {
      if (root.itemTemplate.id === id || root.empty?.id === id) return root;
      const found = findParentOf(root.itemTemplate, id);
      if (found) return found;
      if (root.empty) return findParentOf(root.empty, id);
      break;
    }
    case 'slot':
      if (root.defaultChild?.id === id) return root;
      if (root.defaultChild) return findParentOf(root.defaultChild, id);
      break;
    default:
      break;
  }
  return undefined;
}

export const editorActions = editorSlice.actions;
export const editorReducer = editorSlice.reducer;
